import { useMemo } from 'react';
import type { RevenueByUpstreamResponse } from '@nadoutong/contracts';
import { usePricing } from '../../api/pricing.ts';
import { revenueWindowUnavailable } from '../../components/pricingErrors.ts';
import {
  useRevenueByUpstream,
  useRevenueByUpstreamSeries,
  useThroughputByUpstreamSeries,
} from '../../api/revenue.ts';
import { useUpstreamsList } from '../../api/useUpstreams.ts';
import { useT } from '../../i18n/index.ts';
import { Skeleton } from '../../ui/index.ts';
import {
  GroupAggregateTable,
  GroupTotalsStrip,
  GroupTrendsPanel,
} from './GroupSeriesSection.tsx';
import type { GroupAggregateRow, MetricsGroup } from './GroupSeriesSection.tsx';
import { planGroupSeries } from './hubState.ts';
import type { MetricsTabContext } from './hubState.ts';
import { ThroughputTrendsPanel } from './ThroughputSection.tsx';
import type { ThroughputGroup } from './ThroughputSection.tsx';

/**
 * 上游 SKU tab (#1675): full-fleet per-upstream revenue — window
 * totals, a sortable aggregate table, and an independent trend per
 * upstream (no top-N). Two queries share ONE set of (possibly clamped)
 * bounds from `planGroupSeries`, so the table and the trends always
 * describe the same window:
 *
 *   - `/by-upstream/series` feeds the KPI strip + trend grid;
 *   - `/by-upstream` feeds the aggregate table — unlike the platform
 *     SKU tab (which derives its table from series totals), this
 *     endpoint exists precisely for this table and carries per-ledger-
 *     row market-rate CNY conversions + server-computed margins, which
 *     a display-rate derivation cannot reproduce.
 *
 * Display names join client-side from the gateway upstream registry
 * (`/v1/ops/upstreams`, admin surface); unknown ids and the
 * `'(unknown)'` bucket fall back to the raw label. Each section
 * degrades independently on fetch failure.
 *
 * A third, independent `/by-upstream/throughput-series` request
 * (#1698) feeds the RPM/TPM panel below the money trends — same plan,
 * separate loading/error state (a throughput failure never degrades
 * the money sections, and vice versa).
 */
export function UpstreamTab({ context }: { readonly context: MetricsTabContext }): JSX.Element {
  const t = useT();
  const { window, brand, range } = context;
  const plan = useMemo(() => planGroupSeries(window, range), [window, range]);
  const series = useRevenueByUpstreamSeries({
    granularity: plan.granularity,
    brand,
    from: plan.from,
    to: plan.to,
  });
  const aggregate = useRevenueByUpstream({ brand, from: plan.from, to: plan.to });
  const throughput = useThroughputByUpstreamSeries({
    granularity: plan.granularity,
    brand,
    from: plan.from,
    to: plan.to,
  });
  const upstreams = useUpstreamsList();
  const pricing = usePricing();
  const cnyPerUsd = pricing.data?.policy?.cny_per_usd ?? null;
  const rateValid = typeof cnyPerUsd === 'number' && Number.isFinite(cnyPerUsd) && cnyPerUsd > 0;
  const displayRate = rateValid ? cnyPerUsd : null;

  const nameByUpstreamId = useMemo(() => {
    const map = new Map<string, string>();
    for (const upstream of upstreams.data ?? []) {
      map.set(upstream.id, upstream.name);
    }
    return map;
  }, [upstreams.data]);

  const groups: readonly MetricsGroup[] = useMemo(() => {
    if (series.data === undefined) return [];
    return series.data.series.map((entry) => ({
      key: entry.label,
      name:
        entry.upstream_id === null
          ? entry.label
          : nameByUpstreamId.get(entry.upstream_id) ?? entry.label,
      series: entry,
    }));
  }, [series.data, nameByUpstreamId]);

  const aggregateRows = useMemo(
    () =>
      aggregate.data === undefined
        ? []
        : buildUpstreamAggregateRows(aggregate.data, nameByUpstreamId),
    [aggregate.data, nameByUpstreamId],
  );

  const throughputGroups: readonly ThroughputGroup[] = useMemo(() => {
    if (throughput.data === undefined) return [];
    return throughput.data.series.map((entry) => ({
      key: entry.label,
      name:
        entry.upstream_id === null
          ? entry.label
          : nameByUpstreamId.get(entry.upstream_id) ?? entry.label,
      series: entry,
    }));
  }, [throughput.data, nameByUpstreamId]);

  return (
    <div className="flex flex-col gap-6" data-testid="metrics-upstream-tab">
      {plan.clamped ? (
        <p
          className="m-0 rounded-lg border border-warn-line bg-warn-dim/30 p-3 text-body text-warn"
          data-testid="metrics-upstream-clamp-note"
        >
          {t('metrics.group.clamp_note')}
        </p>
      ) : null}
      {plan.granularity === 'hour' ? (
        <p className="m-0 text-micro text-paper-low" data-testid="metrics-upstream-hourly-note">
          {t('metrics.group.hourly_note')}
        </p>
      ) : null}
      {series.isPending ? (
        <Skeleton className="h-24" testId="metrics-upstream-loading" />
      ) : series.isError ? (
        <p className="m-0 text-body text-bad-bright" data-testid="metrics-upstream-error">
          {t('metrics.group.error')}
        </p>
      ) : series.data !== undefined ? (
        <GroupTotalsStrip groups={groups} cnyPerUsd={displayRate} testIdPrefix="metrics-upstream" />
      ) : null}
      {aggregate.isPending ? (
        <Skeleton className="h-40" testId="metrics-upstream-table-loading" />
      ) : aggregate.isError ? (
        <p className="m-0 text-body text-bad-bright" data-testid="metrics-upstream-table-error">
          {t('metrics.upstream.table.error')}
        </p>
      ) : aggregate.data !== undefined ? (
        <GroupAggregateTable
          caption={t('metrics.upstream.table.title')}
          sub={t('metrics.upstream.table.sub')}
          note={t('metrics.models.table.cost_note')}
          rows={aggregateRows}
          emptyLabel={t('metrics.group.empty')}
          testIdPrefix="metrics-upstream"
        />
      ) : null}
      {series.isError || series.isPending ? null : series.data !== undefined ? (
        <GroupTrendsPanel
          title={t('metrics.upstream.trends.title')}
          groups={groups}
          granularity={series.data.granularity}
          cnyPerUsd={displayRate}
          emptyLabel={t('metrics.group.empty')}
          testIdPrefix="metrics-upstream"
        />
      ) : null}
      {throughput.isPending ? (
        <Skeleton className="h-56" testId="metrics-upstream-throughput-loading" />
      ) : throughput.isError ? (
        <p
          className="m-0 text-body text-bad-bright"
          data-testid="metrics-upstream-throughput-error"
        >
          {revenueWindowUnavailable(throughput.error)
            ? t('metrics.throughput.window_unavailable')
            : t('metrics.throughput.error')}
        </p>
      ) : throughput.data !== undefined ? (
        <ThroughputTrendsPanel
          title={t('metrics.throughput.upstream.title')}
          groups={throughputGroups}
          granularity={throughput.data.granularity}
          windowFrom={throughput.data.window.from}
          windowTo={throughput.data.window.to}
          emptyLabel={t('metrics.group.empty')}
          testIdPrefix="metrics-upstream"
        />
      ) : null}
    </div>
  );
}

/**
 * `/by-upstream` rows → normalised aggregate rows. All CNY figures
 * (including charged and margin) are server-computed per-ledger-row
 * market-rate conversions, so no display-rate fallback is involved.
 */
function buildUpstreamAggregateRows(
  data: RevenueByUpstreamResponse,
  nameByUpstreamId: ReadonlyMap<string, string>,
): readonly GroupAggregateRow[] {
  return data.upstreams.map((row) => ({
    key: row.label,
    name:
      row.upstream_id === null
        ? row.label
        : nameByUpstreamId.get(row.upstream_id) ?? row.label,
    rawId: row.upstream_id,
    usageCount: row.usage_count,
    chargedCnyCents: row.cny_charged_cents,
    costCnyCents: row.cny_upstream_cost_cents,
    profitCnyCents: row.l2_profit_cny_cents,
    marginPct: row.l2_margin_pct,
    costMissingCount: row.cost_missing_count,
  }));
}
